import { memo } from 'react';
import { Heading } from '@/hen-ui/atomics/Heading';
import { Link } from '@/hen-ui/atomics/Link';
import { Button } from '@/hen-ui/atomics/Button';
import { Badge } from '@/hen-ui/atomics/Badge';
import { Icon } from '@/hen-ui/atomics/Icon';
import { Flex } from '@/hen-ui/composites/Flex';
import { rankBadgeVariants } from './ranking-card.variants';
import type { RankingCardProps } from './types';

type RankingCardHeaderProps = Pick<
  RankingCardProps,
  | 'variant'
  | 'rank'
  | 'name'
  | 'nameHref'
  | 'onNameClick'
  | 'superlative'
  | 'badges'
  | 'exploreHref'
  | 'onExploreClick'
  | 'exploreLabel'
>;

export const RankingCardHeader = memo(function RankingCardHeader({
  variant = 'ranked',
  rank,
  name,
  nameHref,
  onNameClick,
  superlative,
  badges,
  exploreHref,
  onExploreClick,
  exploreLabel = 'Explore',
}: RankingCardHeaderProps) {
  // `exploreHref` is the destination signal — a card with no linkable
  // offering has none, so it gates whether the CTA renders at all (a
  // whitespace-only href counts as absent). When a card IS linkable, the
  // optional `onExploreClick` handler owns the click: the CTA renders as a
  // <button> with no href for the browser to follow, so no orphaned second tab
  // can spawn alongside the handler's own linkout window. Without a handler the
  // href renders as a genuine external link.
  const resolvedExploreHref = exploreHref?.trim();
  const showExplore = Boolean(resolvedExploreHref);

  return (
    <Flex
      direction='column'
      direction-md='row'
      gap='xs'
      className='rounded-t-lg'
    >
      <Flex
        direction='column'
        direction-md='row'
        align='stretch'
        gap='xxs'
        className='md:items-center w-full md:gap-0'
      >
        <Flex direction='column' gap='sm' className='flex-grow min-w-0'>
          {superlative && (
            <Badge
              variant='outline'
              size='sm'
              className='inline-flex items-center gap-xxs rounded-xl w-fit font-semibold text-body'
            >
              <Icon
                icon='star'
                size='sm'
                aria-hidden
                className='fill-white stroke-body'
              />
              {superlative}
            </Badge>
          )}
          <Flex direction='row' align='start' gap='sm' className='w-full'>
            <Heading variant='h4' className={rankBadgeVariants({ variant })}>
              {variant === 'ranked' && rank != null && `#${rank}`}
            </Heading>
            <div className='flex-grow w-full'>
              <Heading variant='h3'>
                {nameHref ? (
                  <Link
                    href={nameHref}
                    onClick={onNameClick}
                    className='text-body text-h3 font-bold'
                  >
                    {name}
                  </Link>
                ) : (
                  name
                )}
              </Heading>
              {badges && badges.length > 0 && (
                <Flex
                  direction='row'
                  align='center'
                  gap='xxs'
                  className='flex-wrap mt-xxs'
                >
                  {badges.map((badge, i) => (
                    <Badge
                      key={i}
                      variant='neutral'
                      size='sm'
                      className='text-body font-normal'
                    >
                      {badge}
                    </Badge>
                  ))}
                </Flex>
              )}
            </div>
          </Flex>
        </Flex>
        {showExplore && (
          <div className='flex-shrink-0 md:ml-lg'>
            {onExploreClick ? (
              <Button
                type='button'
                variant='primaryFill'
                size='sm'
                onClick={onExploreClick}
                className='w-full md:w-auto'
              >
                {exploreLabel}
              </Button>
            ) : (
              // Reached only when `showExplore` is true, i.e. the href is set.
              <Button
                variant='primaryFill'
                size='sm'
                href={resolvedExploreHref as string}
                target='_blank'
                rel='noopener noreferrer sponsored'
                className='w-full md:w-auto'
              >
                {exploreLabel}
              </Button>
            )}
          </div>
        )}
      </Flex>
    </Flex>
  );
});
